import { useState } from 'react'
import { Button, Form, Input, Select, Switch, Tag, Space, App } from 'antd'
import { PlusOutlined, EditOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { departmentsApi } from '@/features/masters/departments/api'
import { plantsApi } from '@/features/masters/plants/api'
import type { Department } from '@/shared/types/master'
import DataTable from '@/shared/components/DataTable'
import FormDrawer from '@/shared/components/FormDrawer'
import PageHeader from '@/shared/components/PageHeader'
import { usePermission } from '@/shared/hooks/usePermission'

const DEPT_TYPES = [
  'SPINNING', 'WINDING', 'DOUBLING', 'MAINTENANCE', 'QUALITY',
  'STORES', 'ADMINISTRATION', 'ACCOUNTS', 'HR', 'OTHER',
]

export default function DepartmentsPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [page, setPage] = useState(1)
  const [plantFilter, setPlantFilter] = useState<string | undefined>()
  const [typeFilter, setTypeFilter] = useState<string | undefined>()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editing, setEditing] = useState<Department | null>(null)

  const canCreate = usePermission('department.create')
  const canUpdate = usePermission('department.update')

  const { data, isLoading } = useQuery({
    queryKey: ['departments', page, plantFilter, typeFilter],
    queryFn: () =>
      departmentsApi.list({ page, page_size: 20, plant_id: plantFilter, department_type: typeFilter }),
  })

  const { data: plantsData } = useQuery({
    queryKey: ['plants-all'],
    queryFn: () => plantsApi.list({ page: 1, page_size: 500 }),
  })

  const plantOptions = plantsData?.items.map((p) => ({ label: p.name, value: p.id })) ?? []
  const typeOptions = DEPT_TYPES.map((t) => ({ label: t, value: t }))

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldValue('is_active', true)
    setDrawerOpen(true)
  }

  const openEdit = (record: Department) => {
    setEditing(record)
    form.setFieldsValue(record)
    setDrawerOpen(true)
  }

  const saveMutation = useMutation({
    mutationFn: (values: Omit<Department, 'id' | 'plant_name'>) =>
      editing ? departmentsApi.update(editing.id, values) : departmentsApi.create(values),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['departments'] })
      message.success(editing ? 'Department updated' : 'Department created')
      setDrawerOpen(false)
    },
    onError: () => message.error('Save failed'),
  })

  const onSubmit = () => form.validateFields().then((v) => saveMutation.mutate(v))

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Plant', dataIndex: 'plant_name', key: 'plant_name' },
    { title: 'Type', dataIndex: 'department_type', key: 'department_type' },
    { title: 'Cost Center', dataIndex: 'cost_center_code', key: 'cost_center_code', render: (v?: string) => v ?? '—' },
    {
      title: 'Status',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (v: boolean) => <Tag color={v ? 'green' : 'red'}>{v ? 'Active' : 'Inactive'}</Tag>,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: Department) =>
        canUpdate ? (
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>
            Edit
          </Button>
        ) : null,
    },
  ]

  return (
    <div>
      <PageHeader
        title="Departments"
        subtitle="Manage department master records"
        extra={
          canCreate && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New Department
            </Button>
          )
        }
      />

      <Space style={{ marginBottom: 16 }}>
        <Select
          allowClear
          placeholder="Filter by plant"
          options={plantOptions}
          onChange={(v) => { setPlantFilter(v); setPage(1) }}
          style={{ width: 200 }}
        />
        <Select
          allowClear
          placeholder="Filter by type"
          options={typeOptions}
          onChange={(v) => { setTypeFilter(v); setPage(1) }}
          style={{ width: 160 }}
        />
      </Space>

      <DataTable
        rowKey="id"
        columns={columns}
        dataSource={data?.items}
        loading={isLoading}
        total={data?.total}
        page={page}
        pageSize={20}
        onPageChange={(p) => setPage(p)}
      />

      <FormDrawer
        title={editing ? 'Edit Department' : 'New Department'}
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSubmit={onSubmit}
        loading={saveMutation.isPending}
        form={form}
      >
        <Form.Item name="name" label="Department Name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item name="plant_id" label="Plant" rules={[{ required: true }]}>
          <Select options={plantOptions} placeholder="Select plant" />
        </Form.Item>
        <Form.Item name="department_type" label="Department Type" rules={[{ required: true }]}>
          <Select options={typeOptions} placeholder="Select type" />
        </Form.Item>
        <Form.Item name="cost_center_code" label="Cost Center Code">
          <Input maxLength={30} />
        </Form.Item>
        <Form.Item name="is_active" label="Active" valuePropName="checked">
          <Switch />
        </Form.Item>
      </FormDrawer>
    </div>
  )
}
